import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return d.toLocaleDateString("fr-FR");
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const qc = useQueryClient();

  const { data: count = { count: 0 } } = useQuery({
    queryKey: ["notif-count"],
    queryFn: async () => (await api.get("/me/notifications/unread-count")).data,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const { data: items = [] } = useQuery({
    queryKey: ["notifs"], enabled: open,
    queryFn: async () => (await api.get("/me/notifications")).data,
  });

  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markRead = async (id) => {
    await api.post(`/me/notifications/${id}/read`);
    qc.invalidateQueries({ queryKey: ["notif-count"] });
    qc.invalidateQueries({ queryKey: ["notifs"] });
  };
  const markAll = async () => {
    await api.post("/me/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notif-count"] });
    qc.invalidateQueries({ queryKey: ["notifs"] });
  };

  const linkFor = (n) => {
    if (n.link) {
      try {
        const u = new URL(n.link);
        return u.pathname + u.search;
      } catch { return n.link; }
    }
    return "/mon-espace";
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        className="relative p-2 text-ocean/75 hover:text-ocean transition"
      >
        <Bell className="w-5 h-5" />
        {count.count > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-corail text-blanc text-[10px] font-bold grid place-items-center"
          >
            {count.count > 9 ? "9+" : count.count}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-80 max-w-[92vw] bg-blanc border border-brume rounded-2xl shadow-matelot overflow-hidden z-50"
        >
          <div className="px-4 py-3 flex items-center justify-between border-b border-brume">
            <div className="font-display font-bold text-ocean">Notifications</div>
            {items.some((n) => !n.read) && (
              <button
                onClick={markAll}
                data-testid="notif-mark-all"
                className="text-[11px] text-bretagne hover:text-ocean inline-flex items-center gap-1"
              >
                <CheckCheck className="w-3 h-3" /> Tout marquer lu
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-bretagne">Pas de notification pour l'instant.</div>
            ) : items.map((n) => (
              <Link
                key={n.notification_id}
                to={linkFor(n)}
                data-testid={`notif-item-${n.notification_id}`}
                onClick={() => { setOpen(false); if (!n.read) markRead(n.notification_id); }}
                className={`block px-4 py-3 border-b border-brume/70 last:border-0 hover:bg-brume/40 transition ${
                  !n.read ? "bg-corail/5" : ""
                }`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-corail mt-1.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-ocean">{n.title}</div>
                    <div className="text-xs text-ocean/75 mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-bretagne/70 mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
